import { Link } from "react-router-dom";
import { ArrowRight, TrendingDown, Sparkles, Wallet } from "lucide-react";

/**
 * Promotional banner that drives traffic from any page (city pages, etc.) to
 * /pourquoi-nous, the comparison page with the savings simulator.
 *
 * The banner is intentionally bold (gold + navy) so it catches the eye on city
 * pages where visitors are evaluating their options.
 */
const ComparisonBanner = ({ cityName }) => {
  const headline = cityName
    ? `Vous avez déjà une conciergerie à ${cityName} ?`
    : "Vous avez déjà une conciergerie ?";

  return (
    <section
      className="py-16 lg:py-20 bg-cream"
      data-testid="comparison-banner-section"
    >
      <div className="container-tm">
        <div
          className="relative bg-ink text-white rounded-3xl overflow-hidden grain-overlay"
          data-testid="comparison-banner"
        >
          {/* Decorative blurs */}
          <div className="absolute -top-24 -right-24 w-[420px] h-[420px] bg-gold/20 rounded-full blur-[140px] pointer-events-none" />
          <div className="absolute -bottom-20 -left-20 w-[320px] h-[320px] bg-rose-500/10 rounded-full blur-[120px] pointer-events-none" />

          <div className="relative grid lg:grid-cols-12 gap-8 lg:gap-12 p-8 md:p-12 lg:p-14 items-center">
            {/* Left, copy */}
            <div className="lg:col-span-7">
              <span className="overline text-gold inline-flex items-center gap-2">
                <TrendingDown size={12} strokeWidth={2} />
                Comparatif transparent · Simulateur d'économies
              </span>

              <h3 className="mt-5 font-serif font-light text-white text-3xl md:text-4xl lg:text-[44px] leading-[1.05] tracking-tight">
                {headline}
                <br />
                <span className="italic text-gold">
                  Voyez ce qu'elle vous coûte vraiment.
                </span>
              </h3>

              <p className="mt-5 font-sans text-base md:text-lg text-white/65 leading-relaxed max-w-xl">
                Glissez un curseur, comparez votre commission actuelle avec notre tarif unique de <span className="text-gold font-semibold">15 % TTC</span>. Le résultat est immédiat, et souvent 1 000 à 5 000 € d'économies par an.
              </p>

              {/* Mini stats inline */}
              <div className="mt-7 flex flex-wrap items-center gap-x-7 gap-y-3">
                <MiniBadge icon={Wallet} value="15 %" label="Commission unique" />
                <MiniBadge icon={Sparkles} value="24h" label="Mise en ligne" />
                <MiniBadge value="24/7" label="Permanence de nuit" />
              </div>

              <Link
                to="/pourquoi-nous"
                className="btn-accent group mt-8 inline-flex"
                data-testid="comparison-banner-cta"
              >
                Comparer & calculer mon économie
                <ArrowRight
                  size={14}
                  strokeWidth={2}
                  className="ml-3 group-hover:translate-x-1 transition-transform"
                />
              </Link>
            </div>

            {/* Right, visual mock card */}
            <div className="lg:col-span-5">
              <div
                className="bg-white/[0.04] backdrop-blur-md border border-white/10 rounded-2xl p-6 md:p-7 shadow-lift"
                aria-hidden="true"
              >
                <div className="flex items-center justify-between mb-5 pb-4 border-b border-white/10">
                  <p className="text-[10px] uppercase tracking-[0.25em] font-semibold text-white/55">
                    Exemple, 25 000 € / an
                  </p>
                  <span className="text-[10px] uppercase tracking-[0.2em] text-gold font-semibold">
                    Indicatif
                  </span>
                </div>

                {/* Concurrent row */}
                <div className="mb-5">
                  <div className="flex items-baseline justify-between mb-2">
                    <p className="text-sm text-white/85 font-sans">
                      Conciergerie classique (22 %)
                    </p>
                    <p className="font-serif text-xl text-rose-300 tabular-nums">
                      5 500 €
                    </p>
                  </div>
                  <div className="h-2.5 bg-white/8 rounded-full overflow-hidden">
                    <div className="h-full bg-rose-400 rounded-full w-[100%]" />
                  </div>
                </div>

                {/* TM row */}
                <div className="mb-6">
                  <div className="flex items-baseline justify-between mb-2">
                    <p className="text-sm text-white font-sans font-semibold">
                      Terre et Mer (15 %)
                    </p>
                    <p className="font-serif text-xl text-gold tabular-nums">
                      3 750 €
                    </p>
                  </div>
                  <div className="h-2.5 bg-white/8 rounded-full overflow-hidden">
                    <div className="h-full bg-gold rounded-full w-[68%]" />
                  </div>
                </div>

                {/* Savings highlight */}
                <div
                  className="rounded-xl p-4 flex items-baseline justify-between"
                  style={{
                    background:
                      "linear-gradient(135deg, rgba(224,184,114,0.18), rgba(224,184,114,0.06))",
                    border: "1px solid rgba(224,184,114,0.3)",
                  }}
                >
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.25em] text-gold font-semibold">
                      Économie sur 5 ans
                    </p>
                    <p className="text-xs text-white/55 mt-0.5">
                      … à investir dans votre bien.
                    </p>
                  </div>
                  <p className="font-serif text-3xl text-gold tabular-nums leading-none">
                    8 750 €
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const MiniBadge = ({ icon: Icon, value, label }) => (
  <div className="flex items-center gap-2.5">
    {Icon ? (
      <div className="w-8 h-8 rounded-full bg-gold/15 border border-gold/30 flex items-center justify-center shrink-0">
        <Icon size={14} strokeWidth={1.8} className="text-gold" />
      </div>
    ) : (
      <div className="w-8 h-8 rounded-full bg-gold/15 border border-gold/30 flex items-center justify-center shrink-0">
        <span className="text-[9px] font-serif text-gold leading-none">{value}</span>
      </div>
    )}
    <div>
      <p className="font-serif text-base text-white leading-none">{value}</p>
      <p className="text-[10px] uppercase tracking-[0.18em] text-white/55 font-semibold mt-1 leading-none">
        {label}
      </p>
    </div>
  </div>
);

export default ComparisonBanner;
